/* FNDRY — FAQ accordions
   Plain JS, no framework.

   Every answer ships EXPANDED from Liquid and this script closes them on init.
   That way a script that never runs, or throws, leaves the whole page readable
   instead of sealing 23 answers shut — the same failure direction the footer
   accordions and the hover-select lists are built to. aria-hidden and
   tabindex="-1" on collapsed content are set here and never in Liquid, for the
   same reason. */
(function () {
  'use strict';

  var ROOT = '[data-fndry-faq]';
  var FOCUSABLE = 'a[href], button, input, select, textarea, iframe, [tabindex]';

  function slice(list) {
    return Array.prototype.slice.call(list || []);
  }

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Collapsed answers must leave the tab order, or a link inside one is a
     control the eye can't see and the finger can't reach. Idempotent: the
     original tabindex (or its absence) is parked on data-faq-tabbed. */
  function setFocusable(panel, open) {
    slice(panel.querySelectorAll(FOCUSABLE)).forEach(function (el) {
      if (open) {
        if (el.hasAttribute('data-faq-tabbed')) {
          var prev = el.getAttribute('data-faq-tabbed');
          if (prev === '') el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', prev);
          el.removeAttribute('data-faq-tabbed');
        }
        return;
      }
      if (!el.hasAttribute('data-faq-tabbed')) {
        el.setAttribute('data-faq-tabbed', el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '');
      }
      el.setAttribute('tabindex', '-1');
    });
  }

  function Faq(root) {
    this.root = root;
    this.single = root.getAttribute('data-faq-single') === 'true';
    this.items = slice(root.querySelectorAll('[data-faq-item]'));
    this.bind();
    this.start();
  }

  Faq.prototype.setItem = function (item, open) {
    var trigger = item.querySelector('[data-faq-trigger]');
    var panel = item.querySelector('[data-faq-panel]');

    item.setAttribute('data-collapsed', open ? 'false' : 'true');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) {
      /* Zero height from grid-template-rows still leaves the text in the
         accessibility tree — without this a screen reader reads every answer
         on the page as if it were open. */
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      setFocusable(panel, open);
    }
  };

  Faq.prototype.toggle = function (item) {
    var open = item.getAttribute('data-collapsed') !== 'true';
    if (this.single && !open) {
      this.items.forEach(function (other) {
        if (other !== item) this.setItem(other, false);
      }, this);
    }
    this.setItem(item, !open);
  };

  Faq.prototype.byHash = function () {
    var hash = window.location.hash;
    if (!hash || hash.length < 2) return null;
    var id = hash.slice(1);
    for (var i = 0; i < this.items.length; i++) {
      var item = this.items[i];
      var panel = item.querySelector('[data-faq-panel]');
      if (item.id === id || (panel && panel.id === id)) return item;
    }
    return null;
  };

  Faq.prototype.reveal = function (item) {
    this.setItem(item, true);
    /* The browser already scrolled to the anchor while the item was still
       collapsed, so the height it just gained has moved the target. */
    if (item.scrollIntoView) {
      item.scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
    }
  };

  Faq.prototype.bind = function () {
    var self = this;

    this.items.forEach(function (item) {
      var trigger = item.querySelector('[data-faq-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', function () {
        self.toggle(item);
      });
    });

    this.onHash = function () {
      var item = self.byHash();
      if (item) self.reveal(item);
    };
    window.addEventListener('hashchange', this.onHash);
  };

  Faq.prototype.start = function () {
    var self = this;
    this.items.forEach(function (item) {
      self.setItem(item, false);
    });

    /* A support agent linking a customer straight to one answer beats sending
       them to the page and saying "scroll". */
    var target = this.byHash();
    if (target) {
      this.reveal(target);
      return;
    }

    if (this.root.getAttribute('data-faq-open-first') === 'true' && this.items.length) {
      this.setItem(this.items[0], true);
    }
  };

  function openAll(root) {
    slice(root.querySelectorAll('[data-faq-item]')).forEach(function (item) {
      item.setAttribute('data-collapsed', 'false');
      var trigger = item.querySelector('[data-faq-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      var panel = item.querySelector('[data-faq-panel]');
      if (panel) {
        panel.setAttribute('aria-hidden', 'false');
        setFocusable(panel, true);
      }
    });
  }

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var found = slice(context.querySelectorAll(ROOT));
    if (context.matches && context.matches(ROOT)) found.unshift(context);

    found.forEach(function (root) {
      if (root.getAttribute('data-faq-ready') === 'true') return;
      root.setAttribute('data-faq-ready', 'true');
      try {
        root.fndryFaq = new Faq(root);
      } catch (error) {
        /* Never leave the page half-bound with answers sealed shut. */
        openAll(root);
        if (window.console && console.error) console.error('[fndry-faq]', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!target || !target.querySelector) return;
    var root = target.matches && target.matches(ROOT) ? target : target.querySelector(ROOT);
    if (!root) return;
    if (root.fndryFaq && root.fndryFaq.onHash) {
      window.removeEventListener('hashchange', root.fndryFaq.onHash);
    }
    root.removeAttribute('data-faq-ready');
  });

  /* Selecting a block in the editor opens and scrolls to it — a topic block
     scrolls to its heading, a question block opens its answer. */
  document.addEventListener('shopify:block:select', function (event) {
    var block = event.target;
    if (!block || !block.closest) return;
    var root = block.closest(ROOT);
    if (!root) return;

    init(root.parentNode || document);

    if (block.hasAttribute('data-faq-item')) {
      if (root.fndryFaq) root.fndryFaq.reveal(block);
      return;
    }

    if (block.scrollIntoView) {
      block.scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
    }
  });
})();
